<script setup lang="ts">

import * as ss from 'simple-statistics';
import { ref } from 'vue';

// SOURCE DATA
// -----------
// https://docs.google.com/spreadsheets/d/1eoGBF2g0QQqQH7Y29MY1DCRqbXZvf2LY7yAbAsGafCg/edit?pli=1&gid=1989806954#gid=1989806954
// https://www.slickcharts.com/sp500/returns
// https://curvo.eu/backtest/en/compare-indexes/ftse-100-vs-sp-500?currency=gbp#summary
// https://www.investopedia.com/inflation-rate-by-year-7253832
// https://www.slickcharts.com/inflation
// https://www.ons.gov.uk/economy/inflationandpriceindices/timeseries/l55o/mm23
// https://www.macrotrends.net/global-metrics/countries/gbr/united-kingdom/inflation-rate-cpi
// https://www.ons.gov.uk/economy/inflationandpriceindices/datasets/consumerpriceinflationhistoricaldatauk1950to1988

function unityRatioToPercent(ur: number|string|undefined): string {
  if (ur === undefined || typeof ur === 'string') {
    return '';
  }

  return `${Math.round(ur * 1000) / 10}%`;
}

function geometricMeanRate(rates: number[]): number {
  const product = rates
    .map(r => 1 + r)
    .reduce((accumulator, current) => current * accumulator, 1);

  const root = Math.pow(product, 1 / rates.length);
  return root - 1;
}

const usRealAnnualUntaxedTotalReturnRate = ref([
  [2024, 0.215025025511444],
  [2023, 0.221668681983071],
  [2022, -0.230899272129608],
  [2021, 0.202672397682676],
  [2020, 0.167883211678832],
  [2019, 0.285400068429542],
  [2018, -0.0616750895441832],
  [2017, 0.193183487586308],
  [2016, 0.0967331145613948],
  [2015, 0.00660279005113451],
  [2014, 0.128100813653503],
  [2013, 0.304334975369458],
  [2012, 0.140385371608336],
  [2011, -0.0084482423771608],
  [2010, 0.133596059113301],
  [2009, 0.231233570246325],
  [2008, -0.370597931964634],
  [2007, 0.0134498991257566],
  [2006, 0.129438158408115],
  [2005, 0.0145053669857846],
  [2004, 0.0735863671572425],
  [2003, 0.262930611443714],
  [2002, -0.239183514015041],
  [2001, -0.132562146197391],
  [2000, -0.120847236326708],
  [1999, 0.178693154153277],
  [1998, 0.265488903105162],
  [1997, 0.311307767944936],
  [1996, 0.190204239667022],
  [1995, 0.341982052282481],
  [1994, -0.0132930807810293],
  [1993, 0.0715989291798491],
  [1992, 0.0458697764820215],
  [1991, 0.265715948777648],
  [1990, -0.0867536873851375],
  [1989, 0.25868578255675],
  [1988, 0.116847045302174],
  [1987, 0.00799693530623014],
  [1986, 0.173788328387735],
  [1985, 0.26907514450867],
  [1984, 0.0225643492903536],
  [1983, 0.180789055349487],
  [1982, 0.170832731300872],
  [1981, -0.126893765494445],
  [1980, 0.176962047817972],
  [1979, 0.0454124189063947],
  [1978, -0.0224750022933677],
  [1977, -0.13008434864105],
  [1976, 0.180778032036613],
  [1975, 0.283202394313506],
  [1974, -0.345352564102564],
  [1973, -0.214939515201693],
  [1972, 0.150621343261931],
  [1971, 0.10674347678753],
  [1970, -0.014916891603921],
  [1969, -0.138418079096045],
  [1968, 0.06064368255181],
  [1967, 0.203455639681615],
  [1966, -0.130846540394279],
  [1965, 0.103424590324797],
  [1964, 0.153438629499431],
  [1963, 0.208423538673489],
  [1962, -0.0991462271134581],
  [1961, 0.260267169886279],
  [1960, -0.00897612941408577],
  [1959, 0.100722607285061],
  [1958, 0.408528198074278],
  [1957, -0.132944606413994],
  [1956, 0.0346133307442109],
  [1955, 0.310554365692086],
  [1954, 0.53726833199033],
  [1953, -0.0170265574584264],
  [1952, 0.174596874224758],
  [1951, 0.17],
  [1950, 0.2435443516027],
  [1949, 0.213195118214778],
  [1948, 0.0243215690082044],
  [1947, -0.0285793052747658],
  [1946, -0.221690725140753],
  [1945, 0.33470286133529],
  [1944, 0.170576735092864],
  [1943, 0.222567488832783],
  [1942, 0.103884786497271],
  [1941, -0.195651185006596],
  [1940, -0.104115982324612],
  [1939, -0.00409999999999999],
  [1938, 0.34883242464767],
  [1937, -0.368487558320373],
  [1936, 0.32038452058171],
  [1935, 0.433758920335939],
  [1934, -0.0290611762387941],
  [1933, 0.527981742409208],
  [1932, 0.0233517249066488],
  [1931, -0.375234314698423],
  [1930, -0.197649572649573],
  [1929, -0.0895715279848892],
  [1928, 0.452954269526507],
  [1927, 0.406691221608349],
  [1926, 0.128843042071197],
]);

const ukRealAnnualUntaxedTotalReturnRate = ref([
  [2024, 0.0612778315585674],
  [2023, 0.0103469263542302],
  [2022, -0.0300250208507089],
  [2021, 0.155009267388547],
  [2020, -0.124511114411604],
  [2019, 0.152968934329532],
  [2018, -0.108167554621438],
  [2017, 0.0909534022226555],
  [2016, 0.178555517053611],
  [2015, -0.017183842207501],
  [2014, -0.00744025622074396],
  [2013, 0.159880737083924],
  [2012, 0.0716966418092313],
  [2011, -0.0579793893864972],
  [2010, 0.0987853066003219],
  [2009, 0.248578152578937],
  [2008, -0.307603130132354],
  [2007, 0.0203623223790224],
  [2006, 0.103629976580796],
  [2005, 0.177824575150595],
  [2004, 0.0749050742147051],
  [2003, 0.151001084919617],
  [2002, -0.204807408137129],
  [2001, -0.153350071382858],
]);

</script>

<template>
  <div activityName="Market Growth">
    <h2>Market Growth Explainer</h2>
    <div class="explainer-card">
      <div>
        This page may help you choose asset growth rates and uncertainties for
        your retirement modelling.
        <br/>
        The table below shows historical "total return" rates in "real" terms.
        What do we mean by this?
        <br/>
        Typical investments can deliver returns through two channels: dividends
        and capital gains.
        <br/>
        <ul>
          <li>
            "Total return" means the profit returned by the sum of both
            channels.
          </li>
          <li>
            "Real" means net of inflation. This site's philosophy is to model
            net of inflation, because you're not interested in knowing you
            might have ten pounds thirty years from now, if those ten pounds
            can only buy the same amount of food as your one pound today.
          </li>
        </ul>
      </div>
      <h3>
        untaxed total real return rates
      </h3>
      <table class="rates">
        <thead>
          <tr>
            <th> statistic </th>
            <th> USA </th>
            <th> UK </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td title="This rate, if applied consistently would achieve the same lifetime growth as the observed historic rates. It is calculated by adding one to each of the observations, multiplying them all together, then calculating the n'th root, before subtracting one. This is the more realistic average rate to use.">
              geometric mean
            </td>
            <td>
              {{unityRatioToPercent(geometricMeanRate(usRealAnnualUntaxedTotalReturnRate.map(r => r[1])))}}
            </td>
            <td>
              {{unityRatioToPercent(geometricMeanRate(ukRealAnnualUntaxedTotalReturnRate.map(r => r[1])))}}
            </td>
          </tr>
          <tr>
            <td title="Calculated by summing the rates and then dividing by n. If you use this rate you will be overestimating the lifetime growth of your investments.">
              mean
            </td>
            <td>
              {{unityRatioToPercent(ss.mean(usRealAnnualUntaxedTotalReturnRate.map(r => r[1])))}}
            </td>
            <td>
              {{unityRatioToPercent(ss.mean(ukRealAnnualUntaxedTotalReturnRate.map(r => r[1])))}}
            </td>
          </tr>
          <tr>
            <td title="A measure of the variability of return. Higher variability increases the risk that a few bad years can wipe out our funds early in your retirement, in combination with your withdrawals, meaning there's not enough capital left for later, even if the average rate of return over your retirement is high. This is known as sequencing risk.">
              standard deviation
            </td>
            <td>
              {{unityRatioToPercent(ss.standardDeviation(usRealAnnualUntaxedTotalReturnRate.map(r => r[1])))}}
            </td>
            <td>
              {{unityRatioToPercent(ss.standardDeviation(ukRealAnnualUntaxedTotalReturnRate.map(r => r[1])))}}
            </td>
          </tr>
        </tbody>
        <thead>
          <tr>
            <th> year </th>
            <th> USA </th>
            <th> UK </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="usYearRow in usRealAnnualUntaxedTotalReturnRate.map(r => [r[0], unityRatioToPercent(r[1])])">
            <td> {{usYearRow[0]}} </td>
            <td> {{usYearRow[1]}} </td>
            <td>
              {{
                unityRatioToPercent(
                  (
                    ukRealAnnualUntaxedTotalReturnRate.find(
                      ukr => ukr[0] === usYearRow[0],
                    ) ?? [0, undefined]
                  )[1]
                )
              }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>

td, tr, table {
  border-color: black;
  border-style: solid;
  border-width: 2px;
  padding: 2px;
  border-collapse: collapse;
  text-align: center;
}

</style>
